import { AllowedPartners, useExpertUniversityStore } from "@expert/expert-university";
import { getLogger } from "@expert/logging";
import { trackFullStoryEvent } from "@expert/monitoring";
import { Partner } from "@expert/shared-types";
import { HTTPError } from "ky";

import type { CreateTrainingSessionRequest, TrainingSession, UpdateTrainingSessionRequest } from "../types";
import type { TranscriptContent, TranscriptionEntry } from "./evaluations/types";

import { getLiveKitSessionApiClient } from "./liveKitSessionApi";
import { getTrainingSessionApiClient } from "./trainingSessionApi";

const logger = getLogger({ module: "Expert University API" });

interface TrainingSessionApiResult {
    count: number;
    items: TrainingSession[];
}

interface CreateLiveKitSessionRequest {
    personaId: string;
    userId: string;
    platformId: string;
    trainingSessionId: string;
    partner: Partner;
}

interface EndLiveKitSessionRequest {
    sessionId: string;
}

interface CreateLiveKitSessionResponse {
    sessionId: string;
    lkUrl: string;
    userToken: string;
    roomName: string;
    userId: string;
}

interface EvaluationByIdApiResult {
    count: number;
    items: TranscriptContent[];
}

// The training-session "update" route is a partial DynamoDB UpdateItem keyed by SessionID, shared by
// two callers: session-end updates (UpdateTrainingSessionRequest) and transcript flushes (this type).
// Only SessionID + Transcriptions are sent; other attributes on the item are left untouched.
interface UpdateTrainingSessionTranscriptionsRequest {
    SessionID: string;
    Transcriptions: TranscriptionEntry[];
}

const getErrorDetails = (error: unknown) => {
    if (error instanceof HTTPError) {
        return {
            error: error.message,
            statusCode: error.response.status,
            statusText: error.response.statusText,
        };
    }

    return {
        error: error instanceof Error ? error.message : String(error),
    };
};

const { sessionId: sessionIdFromStore } = useExpertUniversityStore.getState();

export const createLiveKitSession = async (
    request: CreateLiveKitSessionRequest,
): Promise<CreateLiveKitSessionResponse> => {
    try {
        logger.info({ ...request }, "Creating live kit session");
        const response = await getLiveKitSessionApiClient().post(`api/v1/training-assist/sessions`, {
            json: request,
        });
        return response.json();
    } catch (error: unknown) {
        logger.error({ ...getErrorDetails(error), personaId: request.personaId }, "Error creating live kit session");
        throw error;
    }
};

export const createTrainingSession = async (request: CreateTrainingSessionRequest) => {
    try {
        logger.info({ ...request, sessionId: request.SessionID }, "Creating training session");
        await getTrainingSessionApiClient()
            .extend({
                hooks: {
                    beforeRetry: [
                        ({ error, retryCount }) => {
                            logger.warn(
                                {
                                    ...getErrorDetails(error),
                                    retryCount,
                                    sessionId: request.SessionID,
                                    challengeId: request.ChallengeId,
                                },
                                "Retrying training session creation",
                            );
                        },
                    ],
                },
            })
            .post("create", {
                json: request,
            });
        trackFullStoryEvent("Training Session Created", { sessionId: request.SessionID });
    } catch (error: unknown) {
        logger.error(
            { ...getErrorDetails(error), sessionId: request.SessionID, challengeId: request.ChallengeId },
            "Error creating training session",
        );
        trackFullStoryEvent("Training Session Creation Failed", { sessionId: request.SessionID });
        throw error;
    }
};

export const endLiveKitSession = async (request: EndLiveKitSessionRequest) => {
    try {
        logger.info({ ...request, sessionId: request.sessionId }, "Ending live kit session");
        await getLiveKitSessionApiClient().delete(
            `api/v1/training-assist/sessions/${encodeURIComponent(request.sessionId)}`,
            {
                json: request,
            },
        );
    } catch (error: unknown) {
        logger.error({ ...getErrorDetails(error), sessionId: request.sessionId }, "Error ending live kit session");
    }
};

export const saveTranscriptions = async (sessionId: string, transcriptions: TranscriptionEntry[]): Promise<void> => {
    try {
        logger.info({ sessionId, count: transcriptions.length }, "Saving transcriptions");
        // Reuses the existing training-session "update" route, which does a partial UpdateItem keyed by
        // SessionID. Sending only Transcriptions overwrites that attribute (last-write-wins, so retries
        // can't duplicate) while leaving the evaluation lambda's EvaluationDetails on the same item intact.
        const payload: UpdateTrainingSessionTranscriptionsRequest = {
            SessionID: sessionId,
            Transcriptions: transcriptions,
        };
        await getTrainingSessionApiClient().post("update", { json: payload });
    } catch (error: unknown) {
        logger.warn({ ...getErrorDetails(error), sessionId }, "Error saving transcriptions");
        throw error;
    }
};

export const updateTrainingSession = async (request: UpdateTrainingSessionRequest) => {
    const sessionId = request.SessionID;
    try {
        logger.info({ ...request, sessionId }, "Updating training session");
        trackFullStoryEvent("Training Session Updated", { sessionId });
        await getTrainingSessionApiClient().post("update", {
            json: request,
        });
    } catch (error: any) {
        logger.info({ error: error.message, sessionId }, "Error updating training session");
        trackFullStoryEvent("Training Session Update Failed", { sessionId });
    }
};

export const getTrainingSessionsByEmployeeId = async (
    employeeId: string | null,
    partner: AllowedPartners,
    challengeId?: string,
    sessionId?: string,
): Promise<TrainingSessionApiResult | undefined> => {
    const taskId = sessionId ?? sessionIdFromStore;
    try {
        logger.info({ employeeId, taskId }, "Getting training sessions by employee ID");
        trackFullStoryEvent("Fetching Training Sessions", { employeeId, taskId });
        const response = await getTrainingSessionApiClient().post("query", {
            json: {
                EmployeeId: employeeId,
                ChallengeId: challengeId,
                SessionId: sessionId,
                Partner: partner,
            },
        });
        const trainingSessions = await response.json<TrainingSessionApiResult>();

        return trainingSessions;
    } catch (error: any) {
        logger.info({ error: error.message, taskId }, "Error getting training sessions by employee ID");
        trackFullStoryEvent("Fetching Training Sessions Failed", { employeeId, taskId });
    }
};

export const getEvaluationResultsBySessionId = async (
    sessionId: string,
): Promise<EvaluationByIdApiResult | undefined> => {
    const taskId = sessionId ?? sessionIdFromStore;

    try {
        logger.info({ taskId }, "Getting evaluation results by session ID");
        trackFullStoryEvent("Fetching Evaluation Results", { taskId });
        const response = await getTrainingSessionApiClient().post("query", {
            json: {
                SessionId: sessionId,
            },
        });
        const trainingSession = await response.json<EvaluationByIdApiResult>();
        return trainingSession;
    } catch (error: any) {
        logger.info({ error: error.message, taskId }, "Error getting evaluation results by session ID");
        trackFullStoryEvent("Fetching Evaluation Results Failed", { taskId });
    }
};
